import { useWidthTier } from '@lib/hooks/sizing/element-size';
import React from 'react';
import { rowColumnOptions, rowWidthFor } from './rowColumns';
import css from './TierGroup.module.scss';

// Below this a card's feature lines break mid-phrase ("Access to / StarZero Agents"), so the
// row drops to fewer cards per line rather than squeeze one. The cap a card grows to is in
// the stylesheet.
const MIN_CARD_WIDTH = 300;

interface TierGroupProps {
  children: React.ReactNode;
  gap?: number;
  verticalAlign?: 'start' | 'center' | 'end' | 'stretch';
}

/**
 * A row of equal-width plan cards, centered. Every card in a line gets the same width, at
 * least `MIN_CARD_WIDTH` and at most the stylesheet's cap; when the row is too narrow for all
 * of them it shows fewer per line (see `rowColumnOptions`), and below the mobile breakpoint
 * the stylesheet stacks them full-width.
 */
export const TierGroup: React.FC<TierGroupProps> = ({
  children,
  gap = 12,
  verticalAlign = 'center',
}) => {
  const count = React.Children.count(children);
  const options = rowColumnOptions(count);
  // Widest first, as `useWidthTier` expects: the step is how many of these the row is below.
  const thresholds = options.map((columns) => rowWidthFor(columns, MIN_CARD_WIDTH, gap));
  const [rowRef, step] = useWidthTier<HTMLDivElement>(thresholds);
  const columns = options[Math.min(step, options.length - 1)];

  return (
    <div
      ref={rowRef}
      className={css.tierGroup}
      style={
        { gap: `${gap}px`, alignItems: verticalAlign, '--columns': columns } as React.CSSProperties
      }
    >
      {children}
    </div>
  );
};
